import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { UserProfile, MaxLift, Gender } from '../../types';
import './ProfileView.css';

const ProfileView: React.FC = () => {
  const [user] = useAuthState(auth);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [maxLifts, setMaxLifts] = useState<MaxLift[]>([]);
  const [schools, setSchools] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfile>>({});

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    if (!user?.uid) return;

    try {
      // Load schools
      const schoolsRef = collection(db, 'schools');
      const schoolsSnapshot = await getDocs(schoolsRef);
      const schoolList = schoolsSnapshot.docs.map(doc => doc.id);
      setSchools(schoolList.sort());

      // Load user profile
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const rawProfileData = userDoc.data();
        
        // Handle both web format (name) and iOS/Android format (firstName + lastName)
        let profileData: UserProfile;
        if (rawProfileData?.firstName && rawProfileData?.lastName) {
          // iOS/Android format - use as is
          profileData = {
            id: user.uid,
            firstName: rawProfileData.firstName || '',
            lastName: rawProfileData.lastName || '',
            gender: rawProfileData.gender || Gender.Male,
            dateOfBirth: rawProfileData.dateOfBirth?.toDate?.() || new Date(),
            grade: rawProfileData.grade || '',
            school: rawProfileData.school || '',
            phoneNumber: rawProfileData.phoneNumber || '',
            email: rawProfileData.email || user.email || '',
            heightFeet: rawProfileData.heightFeet || 0,
            heightInches: rawProfileData.heightInches || 0,
            weight: rawProfileData.weight || 0,
            // Backward compatibility fields
            name: `${rawProfileData.firstName} ${rawProfileData.lastName}`.trim(),
            avatar: rawProfileData.avatar || user.photoURL || '',
            dateJoined: rawProfileData.dateJoined?.toDate?.() || rawProfileData.createdAt?.toDate?.() || new Date(),
            totalWorkouts: rawProfileData.totalWorkouts || 0,
            currentStreak: rawProfileData.currentStreak || 0
          };
        } else if (rawProfileData?.name) {
          // Web format - convert to full structure
          const nameParts = rawProfileData.name.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          profileData = {
            id: user.uid,
            firstName: firstName,
            lastName: lastName,
            gender: Gender.Male,
            dateOfBirth: new Date(),
            grade: '',
            school: '',
            phoneNumber: '',
            email: rawProfileData.email || user.email || '',
            heightFeet: 0,
            heightInches: 0,
            weight: 0,
            // Backward compatibility fields
            name: rawProfileData.name,
            avatar: rawProfileData.avatar || user.photoURL || '',
            dateJoined: rawProfileData.dateJoined?.toDate?.() || new Date(),
            totalWorkouts: rawProfileData.totalWorkouts || 0,
            currentStreak: rawProfileData.currentStreak || 0
          };
        } else {
          // Default structure
          profileData = {
            id: user.uid,
            firstName: '',
            lastName: '',
            gender: Gender.Male,
            dateOfBirth: new Date(),
            grade: '',
            school: '',
            phoneNumber: '',
            email: user.email || '',
            heightFeet: 0,
            heightInches: 0,
            weight: 0,
            // Backward compatibility fields
            name: user.displayName || 'Anonymous',
            avatar: user.photoURL || '',
            dateJoined: new Date(),
            totalWorkouts: 0,
            currentStreak: 0
          };
        }
        
        setUserProfile(profileData);
        setEditedProfile(profileData);
      } else {
        // Create default profile
        const defaultProfile: UserProfile = {
          id: user.uid,
          firstName: '',
          lastName: '',
          gender: Gender.Male,
          dateOfBirth: new Date(),
          grade: '',
          school: '',
          phoneNumber: '',
          email: user.email || '',
          heightFeet: 0,
          heightInches: 0,
          weight: 0,
          // Backward compatibility fields
          name: user.displayName || 'Anonymous',
          avatar: user.photoURL || '',
          dateJoined: new Date(),
          totalWorkouts: 0,
          currentStreak: 0
        };
        setUserProfile(defaultProfile);
        setEditedProfile(defaultProfile);
        
        // Save default profile to Firestore
        await setDoc(doc(db, 'users', user.uid), {
          ...defaultProfile,
          dateJoined: new Date()
        });
      }

      // Load max lifts
      const maxLiftsRef = collection(db, 'maxLifts');
      const q = query(
        maxLiftsRef,
        where('userId', '==', user.uid),
        orderBy('dateCreated', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const lifts: MaxLift[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          benchPress: data.benchPress,
          deadlift: data.deadlift,
          squat: data.squat,
          clean: data.clean,
          dateCreated: data.dateCreated.toDate()
        };
      });

      setMaxLifts(lifts);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading user data:', error);
      setErrorMessage(`Failed to load profile: ${error.message}`);
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!user?.uid || !editedProfile) return;

    setSaving(true);
    try {
      // Update the name field from firstName + lastName for backward compatibility
      const updatedProfile = {
        ...editedProfile,
        name: `${editedProfile.firstName || ''} ${editedProfile.lastName || ''}`.trim(),
        id: user.uid,
        dateJoined: userProfile?.dateJoined || new Date()
      };

      await setDoc(doc(db, 'users', user.uid), updatedProfile);
      
      setUserProfile(updatedProfile as UserProfile);
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setErrorMessage(`Failed to save profile: ${error.message}`);
    }
    setSaving(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error: any) {
      console.error('Error signing out:', error);
      setErrorMessage(`Failed to sign out: ${error.message}`);
    }
  };

  const getPersonalRecords = () => {
    if (maxLifts.length === 0) return null;
    
    const latest = maxLifts[0];
    return {
      benchPress: latest.benchPress,
      deadlift: latest.deadlift,
      squat: latest.squat,
      clean: latest.clean,
      total: latest.benchPress + latest.deadlift + latest.squat + latest.clean
    };
  };

  const getProfileProgress = () => {
    if (maxLifts.length < 2) return null;
    
    const latest = maxLifts[0];
    const previous = maxLifts[1];
    
    return {
      benchPress: latest.benchPress - previous.benchPress,
      deadlift: latest.deadlift - previous.deadlift,
      squat: latest.squat - previous.squat,
      clean: latest.clean - previous.clean
    };
  };

  if (isLoading) {
    return (
      <div className="profile-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="profile-view">
        <div className="error-state">
          <p>Unable to load profile</p>
        </div>
      </div>
    );
  }

  const personalRecords = getPersonalRecords();
  const progress = getProfileProgress();

  return (
    <div className="profile-view">
      <div className="profile-header">
        <div className="profile-avatar">
          {userProfile.avatar ? (
            <img src={userProfile.avatar} alt={userProfile.name || 'User'} />
          ) : (
            <div className="avatar-placeholder">
              {(userProfile.name || 'A').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="profile-info">
          <h1>{userProfile.name || 'Anonymous'}</h1>
          <p className="profile-email">{userProfile.email}</p>
          <p className="member-since">
            Member since {userProfile.dateJoined?.toLocaleDateString?.() || 'Unknown'}
          </p>
        </div>
        
        <div className="profile-actions">
          <button 
            className="edit-button"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        </div>
      </div>

      <div className="profile-stats">
        <div className="stat-card">
          <div className="stat-value">{userProfile.totalWorkouts}</div>
          <div className="stat-label">Total Workouts</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{userProfile.currentStreak}</div>
          <div className="stat-label">Current Streak</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{maxLifts.length}</div>
          <div className="stat-label">Max Lift Records</div>
        </div>
      </div>

      {personalRecords && (
        <div className="personal-records">
          <h2>Personal Records</h2>
          <div className="records-grid">
            <div className="record-card bench">
              <div className="record-name">Bench Press</div>
              <div className="record-value">{personalRecords.benchPress} lbs</div>
              {progress && progress.benchPress !== 0 && (
                <div className={`record-change ${progress.benchPress > 0 ? 'positive' : 'negative'}`}>
                  {progress.benchPress > 0 ? '+' : ''}{progress.benchPress} lbs
                </div>
              )}
            </div>
            
            <div className="record-card deadlift">
              <div className="record-name">Deadlift</div>
              <div className="record-value">{personalRecords.deadlift} lbs</div>
              {progress && progress.deadlift !== 0 && (
                <div className={`record-change ${progress.deadlift > 0 ? 'positive' : 'negative'}`}>
                  {progress.deadlift > 0 ? '+' : ''}{progress.deadlift} lbs
                </div>
              )}
            </div>
            
            <div className="record-card squat">
              <div className="record-name">Squat</div>
              <div className="record-value">{personalRecords.squat} lbs</div>
              {progress && progress.squat !== 0 && (
                <div className={`record-change ${progress.squat > 0 ? 'positive' : 'negative'}`}>
                  {progress.squat > 0 ? '+' : ''}{progress.squat} lbs
                </div>
              )}
            </div>
            
            <div className="record-card clean">
              <div className="record-name">Clean</div>
              <div className="record-value">{personalRecords.clean} lbs</div>
              {progress && progress.clean !== 0 && (
                <div className={`record-change ${progress.clean > 0 ? 'positive' : 'negative'}`}>
                  {progress.clean > 0 ? '+' : ''}{progress.clean} lbs
                </div>
              )}
            </div>
          </div>
          
          <div className="total-card">
            <div className="total-label">Total</div>
            <div className="total-value">{personalRecords.total} lbs</div>
          </div>
        </div>
      )}

      <div className="profile-settings">
        <button className="sign-out-button" onClick={handleSignOut}>
          Sign Out
        </button>
      </div>

      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button 
                className="close-button"
                onClick={() => setIsEditing(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editedProfile.firstName || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, firstName: e.target.value})}
                    className="text-field"
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editedProfile.lastName || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, lastName: e.target.value})}
                    className="text-field"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editedProfile.email || ''}
                  className="text-field"
                  placeholder="Enter your email"
                  disabled
                />
                <small className="field-note">Email cannot be changed</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    value={editedProfile.gender || Gender.Male}
                    onChange={(e) => setEditedProfile({...editedProfile, gender: e.target.value as Gender})}
                    className="select-field"
                  >
                    <option value={Gender.Male}>Male</option>
                    <option value={Gender.Female}>Female</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    value={editedProfile.dateOfBirth ? new Date(editedProfile.dateOfBirth).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditedProfile({...editedProfile, dateOfBirth: new Date(e.target.value)})}
                    className="text-field"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Grade</label>
                  <select
                    value={editedProfile.grade || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, grade: e.target.value})}
                    className="select-field"
                  >
                    <option value="">Select Grade</option>
                    <option value="7">7th Grade</option>
                    <option value="8">8th Grade</option>
                    <option value="9">9th Grade</option>
                    <option value="10">10th Grade</option>
                    <option value="11">11th Grade</option>
                    <option value="12">12th Grade</option>
                    <option value="Coach">Coach</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>School</label>
                  <select
                    value={editedProfile.school || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, school: e.target.value})}
                    className="select-field"
                  >
                    <option value="">Select School</option>
                    {schools.map((school) => (
                      <option key={school} value={school}>
                        {school}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={editedProfile.phoneNumber || ''}
                  onChange={(e) => setEditedProfile({...editedProfile, phoneNumber: e.target.value})}
                  className="text-field"
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Height (Feet)</label>
                  <input
                    type="number"
                    value={editedProfile.heightFeet || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, heightFeet: parseInt(e.target.value) || 0})}
                    className="text-field"
                    placeholder="Feet"
                    min="0"
                    max="8"
                  />
                </div>
                <div className="form-group">
                  <label>Height (Inches)</label>
                  <input
                    type="number"
                    value={editedProfile.heightInches || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, heightInches: parseInt(e.target.value) || 0})}
                    className="text-field"
                    placeholder="Inches"
                    min="0"
                    max="11"
                  />
                </div>
                <div className="form-group">
                  <label>Weight (lbs)</label>
                  <input
                    type="number"
                    value={editedProfile.weight || ''}
                    onChange={(e) => setEditedProfile({...editedProfile, weight: parseInt(e.target.value) || 0})}
                    className="text-field"
                    placeholder="Weight"
                    min="0"
                    max="500"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
                <button 
                  className="save-button"
                  onClick={saveProfile}
                  disabled={isSaving || !editedProfile.firstName || !editedProfile.lastName}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ProfileView;
